import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_TYPES, ERROR_MESSAGES, INFO_MESSAGES, SupportedLanguage } from '../constants/messages';
import { StorageHelper } from '../helpers/StorageHelper';
import { ChatFileCategory, LiveChatData, ScreenType } from '../types';

let agentDefaultLanguage: SupportedLanguage | null = null;

export function resolveSupportedLanguage(langCode: string | null | undefined): SupportedLanguage | null {
  if (!langCode) {
    return null;
  }

  const shortCode = langCode.toLowerCase().split('-')[0];
  if (shortCode in ERROR_MESSAGES) {
    return shortCode as SupportedLanguage;
  }

  return null;
}

export function applyAgentDefaultLocaleFromApi(langCode: string | null | undefined): void {
  const resolved = resolveSupportedLanguage(langCode);
  if (resolved) {
    agentDefaultLanguage = resolved;
  }
}

export function resetAgentDefaultLocale(): void {
  agentDefaultLanguage = null;
}

export function hydrateAgentMessageLanguageFromStorage(): void {
  const agentConfig = StorageHelper.getAgentConfigSync();
  applyAgentDefaultLocaleFromApi(agentConfig.language);
}

export const getCustomerLanguage = (chatData: LiveChatData | null | undefined): SupportedLanguage => {
  if (!!chatData) {
    const config = chatData.config;
    if (config?.locale) {
      const lang = config.locale.toLowerCase().substring(0, 2);
      if (lang in ERROR_MESSAGES) {
        return lang as SupportedLanguage;
      }
    }
  }

  return 'en' as SupportedLanguage;
};

const resolveMessageLanguage = (
  chatData: LiveChatData | null | undefined,
  screenType?: ScreenType
): SupportedLanguage => {
  if (screenType === ScreenType.Agent) {
    if (agentDefaultLanguage) {
      return agentDefaultLanguage;
    }

    hydrateAgentMessageLanguageFromStorage();
    if (agentDefaultLanguage) {
      return agentDefaultLanguage;
    }
  }

  return getCustomerLanguage(chatData);
};

export const getErrorMessage = (
  key: keyof typeof ERROR_MESSAGES.en,
  chatData: LiveChatData | null | undefined,
  screenType?: ScreenType
): string => {
  const detectedLanguage = resolveMessageLanguage(chatData, screenType);
  return ERROR_MESSAGES[detectedLanguage]?.[key] || ERROR_MESSAGES.en[key] || key;
};

export const getInfoMessage = (
  key: keyof typeof INFO_MESSAGES.en,
  chatData: LiveChatData | null | undefined,
  screenType?: ScreenType
): string => {
  const detectedLanguage = resolveMessageLanguage(chatData, screenType);
  return INFO_MESSAGES[detectedLanguage]?.[key] || INFO_MESSAGES.en[key] || key;
};

export const setUserLanguagePreference = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem('chat_language', language);
  } catch (e) {
    console.warn('Could not save language preference to localStorage');
  }
};

// File validation utilities
export const isValidFileType = (file: File): boolean => {
  // Check by MIME type first
  if (file.type && file.type in ALLOWED_FILE_TYPES) {
    return true;
  }

  // Fallback: check by file extension
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  return ALLOWED_FILE_EXTENSIONS.includes(fileExtension as any);
};

export const getFileTypeError = (files: File[], chatData?: any): string | null => {
  const invalidFiles = files.filter(file => !isValidFileType(file));

  if (invalidFiles.length > 0) {
    return getErrorMessage('invalidFileType', chatData);
  }

  return null;
};

// File category determination
export const getFileCategory = (file: File): ChatFileCategory => {
  const mimeType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  const extension = fileName.substring(fileName.lastIndexOf('.'));

  // Images
  if (mimeType.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(extension)) {
    return ChatFileCategory.IMAGE;
  }

  // Documents
  if (mimeType.includes('pdf') || mimeType.includes('word') || mimeType.includes('excel') ||
    mimeType.includes('powerpoint') || mimeType.includes('document') ||
    ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].includes(extension)) {
    return ChatFileCategory.DOCUMENT;
  }

  // Text files
  if (mimeType.startsWith('text/') || ['.txt', '.csv'].includes(extension)) {
    return ChatFileCategory.TEXT;
  }

  // Archives
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z') ||
    ['.zip', '.rar', '.7z'].includes(extension)) {
    return ChatFileCategory.ARCHIVE;
  }

  return ChatFileCategory.OTHER;
};

// Get file extension from filename
export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
};
